import {
  platformLabel,
  platformShortLabel,
  type Platform,
} from '@midiautolistening/contracts';
import type {
  AgentCommandStatus,
  AgentStatus,
  AiCapability,
  AiCredentialState,
  AiKeyValidationStatus,
  AiModelTier,
  BrowserStatus,
  CommentJobStatus,
  CommentRejectionReason,
  CommentSource,
  CommentSourceBlocker,
  DomSnapshotPageKind,
  PlatformSessionStatus,
  KeywordMatchType,
  PostIntent,
  PostReviewStatus,
  PostSourceType,
  ProjectStatus,
  SelectorHealthOutcome,
  SelectorPackVersionStatus,
  UnsupportedClaim,
} from '@midiautolistening/contracts';

/**
 * Vietnamese labels and a visual tone for every status vocabulary the dashboard
 * renders.
 *
 * The maps are typed as `Record<Enum, ...>` so adding a member to
 * `contracts/enums.ts` breaks the build here instead of silently rendering a raw
 * SCREAMING_SNAKE value to a user.
 */

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export const toneClass: Record<Tone, string> = {
  neutral: 'tone-neutral',
  success: 'tone-success',
  warning: 'tone-warning',
  danger: 'tone-danger',
  info: 'tone-info',
};

export interface Descriptor {
  label: string;
  tone: Tone;
}

/**
 * Channel badges. Names come from the shared `PLATFORM_CATALOG` — the same
 * source the project form's picker renders from — so a platform added there
 * shows up here without another edit; only the TONE is a dashboard decision.
 */
export const PLATFORM_TONES: Record<Platform, Tone> = {
  FACEBOOK: 'info',
  THREADS: 'neutral',
};

export function platformDescriptor(platform: Platform): Descriptor {
  return { label: platformLabel(platform), tone: PLATFORM_TONES[platform] };
}

/** Short label for tight columns and buttons — "FB", "Threads". */
export function platformShort(platform: Platform): string {
  return platformShortLabel(platform);
}

export const AGENT_STATUS_LABELS: Record<AgentStatus, Descriptor> = {
  UNPAIRED: { label: 'Chưa ghép nối', tone: 'neutral' },
  OFFLINE: { label: 'Ngoại tuyến', tone: 'danger' },
  ONLINE: { label: 'Trực tuyến', tone: 'success' },
  UPDATING: { label: 'Đang cập nhật', tone: 'info' },
  ERROR: { label: 'Lỗi', tone: 'danger' },
};

export const BROWSER_STATUS_LABELS: Record<BrowserStatus, Descriptor> = {
  STOPPED: { label: 'Đã dừng', tone: 'neutral' },
  STARTING: { label: 'Đang khởi động', tone: 'info' },
  LOGIN_REQUIRED: { label: 'Cần đăng nhập', tone: 'warning' },
  READY: { label: 'Sẵn sàng', tone: 'success' },
  RUNNING: { label: 'Đang chạy', tone: 'success' },
  PAUSED: { label: 'Tạm dừng', tone: 'warning' },
  USER_ACTION_REQUIRED: { label: 'Cần bạn xử lý thủ công', tone: 'danger' },
  CRASHED: { label: 'Trình duyệt đã sập', tone: 'danger' },
};

export const SESSION_STATUS_LABELS: Record<PlatformSessionStatus, Descriptor> = {
  UNKNOWN: { label: 'Chưa xác định', tone: 'neutral' },
  LOGGED_OUT: { label: 'Chưa đăng nhập', tone: 'warning' },
  LOGGED_IN: { label: 'Đã đăng nhập', tone: 'success' },
  CHECKPOINT: { label: 'Đang bị checkpoint', tone: 'danger' },
  TWO_FACTOR_REQUIRED: { label: 'Cần xác thực 2 lớp', tone: 'danger' },
  RESTRICTED: { label: 'Tài khoản bị hạn chế', tone: 'danger' },
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, Descriptor> = {
  DRAFT: { label: 'Nháp', tone: 'neutral' },
  READY: { label: 'Sẵn sàng', tone: 'info' },
  STARTING: { label: 'Đang khởi động', tone: 'info' },
  RUNNING: { label: 'Đang chạy', tone: 'success' },
  PAUSED: { label: 'Tạm dừng', tone: 'warning' },
  STOPPING: { label: 'Đang dừng', tone: 'warning' },
  STOPPED: { label: 'Đã dừng', tone: 'neutral' },
  ERROR: { label: 'Lỗi', tone: 'danger' },
};

export const POST_REVIEW_STATUS_LABELS: Record<PostReviewStatus, Descriptor> = {
  NEW: { label: 'Mới', tone: 'info' },
  VIEWED: { label: 'Đã xem', tone: 'neutral' },
  RELEVANT: { label: 'Liên quan', tone: 'success' },
  IRRELEVANT: { label: 'Không liên quan', tone: 'warning' },
  BLOCKED: { label: 'Đã chặn', tone: 'danger' },
  ARCHIVED: { label: 'Lưu trữ', tone: 'neutral' },
};

export const POST_SOURCE_TYPE_LABELS: Record<PostSourceType, string> = {
  PROFILE: 'Trang cá nhân',
  PAGE: 'Fanpage',
  GROUP: 'Nhóm',
  MARKETPLACE: 'Marketplace',
  EVENT: 'Sự kiện',
  UNKNOWN: 'Không rõ',
};

export const POST_INTENT_LABELS: Record<PostIntent, string> = {
  SEEKING_SERVICE: 'Tìm dịch vụ',
  SEEKING_PRODUCT: 'Tìm sản phẩm',
  PRICE_INQUIRY: 'Hỏi giá',
  RECOMMENDATION_REQUEST: 'Xin gợi ý',
  COMPARISON: 'So sánh',
  COMPLAINT: 'Phàn nàn',
  SELLING: 'Rao bán',
  GENERAL_DISCUSSION: 'Thảo luận chung',
  UNRELATED: 'Không liên quan',
};

/** Intents the operator treats as buying signal — backs the "có ý định mua" filter. */
export const PURCHASE_INTENTS: readonly PostIntent[] = [
  'SEEKING_SERVICE',
  'SEEKING_PRODUCT',
  'PRICE_INQUIRY',
  'RECOMMENDATION_REQUEST',
  'COMPARISON',
];

export const KEYWORD_MATCH_TYPE_LABELS: Record<KeywordMatchType, string> = {
  EXACT: 'Chính xác',
  PHRASE: 'Cụm từ',
  BROAD: 'Mở rộng',
};

export const SELECTOR_PACK_VERSION_LABELS: Record<SelectorPackVersionStatus, Descriptor> = {
  DRAFT: { label: 'Nháp', tone: 'neutral' },
  PUBLISHED: { label: 'Đang phát hành', tone: 'success' },
  ROLLED_BACK: { label: 'Đã rollback', tone: 'warning' },
  ARCHIVED: { label: 'Lưu trữ', tone: 'neutral' },
};

export const SELECTOR_HEALTH_OUTCOME_LABELS: Record<SelectorHealthOutcome, Descriptor> = {
  SUCCESS: { label: 'Bình thường', tone: 'success' },
  FALLBACK_USED: { label: 'Phải dùng dự phòng', tone: 'warning' },
  FAILED: { label: 'Hỏng', tone: 'danger' },
};

export const DOM_SNAPSHOT_PAGE_KIND_LABELS: Record<DomSnapshotPageKind, string> = {
  SEARCH_RESULTS: 'Trang kết quả tìm kiếm',
  POST_DETAIL: 'Chi tiết bài viết',
  COMMENT_BOX: 'Ô bình luận',
  LOGIN: 'Trang đăng nhập',
  CHECKPOINT: 'Trang checkpoint',
  OTHER: 'Khác',
};

export const AGENT_COMMAND_STATUS_LABELS: Record<AgentCommandStatus, Descriptor> = {
  PENDING: { label: 'Chờ gửi', tone: 'neutral' },
  SENT: { label: 'Đã gửi tới agent', tone: 'info' },
  ACKNOWLEDGED: { label: 'Agent đã nhận', tone: 'info' },
  COMPLETED: { label: 'Hoàn tất', tone: 'success' },
  FAILED: { label: 'Thất bại', tone: 'danger' },
  REJECTED: { label: 'Bị từ chối', tone: 'danger' },
  EXPIRED: { label: 'Hết hạn', tone: 'warning' },
};

/**
 * What the user must do when a profile lands in USER_ACTION_REQUIRED.
 *
 * The product never attempts a CAPTCHA, a checkpoint or a 2FA prompt. These
 * strings say so explicitly, because a user who thinks the app is "working on it"
 * will wait forever.
 */
export function userActionInstruction(
  status: PlatformSessionStatus,
  platform: Platform = 'FACEBOOK',
): string | null {
  const name = platformLabel(platform);
  const table: Record<PlatformSessionStatus, string | null> = {
    UNKNOWN: null,
    LOGGED_IN: null,
    LOGGED_OUT: `Phiên ${name} đã đăng xuất. Bấm "Mở trang đăng nhập" rồi tự đăng nhập trong cửa sổ Chromium vừa mở.`,
    CHECKPOINT: `${name} đang yêu cầu xác minh (checkpoint). Bạn phải tự hoàn tất trong cửa sổ Chromium — hệ thống cố ý KHÔNG tự vượt qua bước này.`,
    TWO_FACTOR_REQUIRED: `${name} đang hỏi mã xác thực 2 lớp. Bạn phải tự nhập mã trong cửa sổ Chromium — hệ thống cố ý KHÔNG tự nhập hộ.`,
    RESTRICTED: `Tài khoản ${name} đang bị hạn chế. Hãy xử lý trực tiếp với ${name}; hệ thống sẽ giữ project ở trạng thái tạm dừng cho tới khi bạn giải quyết xong.`,
  };
  return table[status];
}

/**
 * The outcome of the last key check.
 *
 * Two of these are deliberately WARNING rather than DANGER: `RATE_LIMITED` and
 * `UNREACHABLE` say nothing about the key — the provider simply never answered
 * the question. Painting them red sends a user off rotating a key that was fine.
 *
 * None of these labels names a provider. They describe what the platform learned,
 * and the provider's own name is rendered from the catalog next to them.
 */
export const AI_KEY_VALIDATION_LABELS: Record<AiKeyValidationStatus, Descriptor> = {
  VALID: { label: 'Khoá dùng được', tone: 'success' },
  KEY_REJECTED: { label: 'Nhà cung cấp từ chối khoá', tone: 'danger' },
  MODEL_NOT_AVAILABLE: { label: 'Model không dùng được', tone: 'danger' },
  RATE_LIMITED: { label: 'Bị giới hạn tần suất — chưa kết luận', tone: 'warning' },
  UNREACHABLE: { label: 'Không gọi được nhà cung cấp — chưa kết luận', tone: 'warning' },
  NOT_CHECKED: { label: 'Lưu mà chưa kiểm', tone: 'warning' },
};

/**
 * Price/quality band of a model. Never a price: the catalog comment explains why
 * a number compiled into the frontend would be wrong within weeks.
 */
/** What a provider or model may be used for, as the catalog reports it. */
export const AI_CAPABILITY_LABELS: Record<AiCapability, string> = {
  POST_SCORING: 'Chấm điểm bài viết',
  COMMENT_GENERATION: 'Soạn bình luận',
  POST_TIME_EXTRACTION: 'Đọc thời gian đăng từ ảnh',
};

export const AI_MODEL_TIER_LABELS: Record<AiModelTier, Descriptor> = {
  ECONOMY: { label: 'Tiết kiệm', tone: 'neutral' },
  BALANCED: { label: 'Cân bằng', tone: 'info' },
  PREMIUM: { label: 'Cao cấp', tone: 'warning' },
};

/**
 * What the server could establish about AI credentials.
 *
 * `UNKNOWN` gets its own label rather than collapsing into "chưa có": the API
 * genuinely cannot see a platform key, and a screen that turns "không đo được"
 * into "không có" is the exact lie this vocabulary exists to prevent.
 */
export const AI_CREDENTIAL_STATE_LABELS: Record<AiCredentialState, Descriptor> = {
  WORKSPACE_KEY: { label: 'Đang dùng khoá của workspace', tone: 'success' },
  PLATFORM_KEY: { label: 'Đang dùng khoá mặc định của hệ thống', tone: 'info' },
  NOT_AVAILABLE: { label: 'Không có khoá AI dùng được', tone: 'danger' },
  // Deliberately not folded into NOT_AVAILABLE: this operator removed a key on
  // purpose and may not have connected that to the AI sources going dark.
  REVOKED: { label: 'Khoá AI đã bị thu hồi', tone: 'danger' },
  UNKNOWN: { label: 'Chưa xác định được', tone: 'warning' },
};

export const BLOCKER_SEVERITY_TONE: Record<CommentSourceBlocker['severity'], Tone> = {
  BLOCKING: 'danger',
  WARNING: 'warning',
};

/**
 * The life of one comment, from drafted to posted.
 *
 * `SUCCESS` is the only state that claims something reached Facebook, so it is
 * the only green one. The four middle states are `info` rather than `success`
 * for the same reason: "đã gửi tới agent" says the command left the server, not
 * that a comment exists — the agent is the only witness to that, and a green
 * chip before its report is a promise the server cannot keep.
 *
 * `SKIPPED` and `CANCELLED` are both neutral and both dead ends, but they are
 * worded apart on purpose: one is a decision the system made, the other one a
 * person made.
 */
export const COMMENT_JOB_STATUS_LABELS: Record<CommentJobStatus, Descriptor> = {
  PENDING: { label: 'Chờ xử lý', tone: 'neutral' },
  WAITING_APPROVAL: { label: 'Chờ bạn duyệt', tone: 'warning' },
  APPROVED: { label: 'Đã duyệt', tone: 'info' },
  QUEUED: { label: 'Đã xếp hàng', tone: 'info' },
  SENT_TO_AGENT: { label: 'Đã gửi tới agent', tone: 'info' },
  EXECUTING: { label: 'Đang đăng', tone: 'info' },
  SUCCESS: { label: 'Đã đăng', tone: 'success' },
  FAILED: { label: 'Thất bại', tone: 'danger' },
  SKIPPED: { label: 'Hệ thống bỏ qua', tone: 'neutral' },
  CANCELLED: { label: 'Bạn đã huỷ', tone: 'neutral' },
  USER_ACTION_REQUIRED: { label: 'Cần bạn xử lý thủ công', tone: 'danger' },
};

/**
 * The button on the comment queue that lifts its default "chỉ bài chờ duyệt"
 * filter — named here so nothing has to describe it from memory.
 *
 * It is a constant because a link on another screen has to tell the operator
 * what to press when they arrive, and the sentence that did so named "Xem tất cả
 * job" — a button that has never existed on `CommentJobsView`. Somebody who
 * followed the link because their post already had a SENT command saw an empty
 * table (the queue opens filtered to WAITING_APPROVAL), went looking for the
 * words they had been given, and found nothing of the sort. Instructions that
 * name a control must break when the control is renamed, and a shared string is
 * the only way to get that.
 */
export const COMMENT_QUEUE_SHOW_ALL_STATUSES_LABEL = 'Bỏ lọc chờ duyệt';

/**
 * Where the body of an EXISTING candidate or job came from. Past tense.
 *
 * `MANUAL` used to read "Bạn tự viết", which was a lie with teeth.
 * `generateCommentRequestSchema` carries no field for text the user typed, and
 * `buildDraft()` in the API branches only on `source === 'AI'`; every other
 * value — MANUAL included — falls through to `pickTemplate()` and comes back
 * holding a TEMPLATE's body. The one thing MANUAL actually buys is
 * `requiresManualReview: true` on that body, i.e. the draft is parked so a
 * person can rewrite it in the approval dialog. So that is what the label says.
 *
 * For the compose form use {@link COMMENT_SOURCE_CHOICES} instead. This map
 * describes what already happened; a form promises what will happen, and MANUAL
 * is precisely the value where those two are not the same sentence.
 */
export const COMMENT_SOURCE_LABELS: Record<CommentSource, string> = {
  MANUAL: 'Mẫu, chờ bạn sửa',
  TEMPLATE: 'Mẫu có sẵn',
  AI: 'AI soạn',
};

export interface CommentSourceChoice {
  value: CommentSource;
  label: string;
  /** What the server will actually do with this choice. Not a slogan. */
  hint: string;
}

/**
 * The only sources the compose dialog may offer.
 *
 * A list, not a map, because the order on screen is a decision and iterating a
 * `Record<CommentSource, …>` would both scramble it and — the reason this
 * constant exists — silently re-offer MANUAL the moment someone adds a label
 * for it. Iterating `COMMENT_SOURCE_LABELS` in a `v-for` is how the dropdown
 * came to offer a source the server cannot honour; see
 * {@link COMMENT_SOURCE_WITHHELD_REASONS}.
 */
export const COMMENT_SOURCE_CHOICES: readonly CommentSourceChoice[] = [
  {
    value: 'TEMPLATE',
    label: 'Lấy từ mẫu có sẵn',
    hint: 'Máy chủ chọn một mẫu đang bật của dự án, ưu tiên mẫu lâu chưa dùng.',
  },
  {
    value: 'AI',
    label: 'Nhờ AI soạn',
    hint: 'Mô hình viết bản nháp mới dựa trên nội dung bài viết và tư liệu thương hiệu của dự án.',
  },
];

/**
 * True of every choice above, so it is said once instead of twice.
 *
 * It is also the sentence the compose screen most needs: at TEMPLATE_AUTO with
 * approval switched off, pressing Soạn *posts a comment* — there is no dialog
 * step in between — and the source dropdown is not what decides that.
 */
export const COMMENT_SOURCE_APPROVAL_CAVEAT =
  'Bản nháp có dừng lại chờ bạn đọc hay đi thẳng lên Facebook là do chế độ bình luận và cấu hình duyệt của dự án quyết định, không phải do lựa chọn nguồn ở đây.';

/**
 * Sources the compose form must not offer, each with the reason, in Vietnamese.
 *
 * MANUAL is withheld because the name promises the one thing the request cannot
 * carry. Two consequences made this worth a constant rather than a comment: on a
 * project with no templates it answers 422 "Không có mẫu comment nào đang bật",
 * which is nonsense to somebody who just said they would write it themselves;
 * and at TEMPLATE_AUTO with approval off it posts a template body the user never
 * saw, under their own name, on a stranger's post.
 *
 * Exported so the dialog can render the reason instead of guessing why the
 * option vanished.
 */
export const COMMENT_SOURCE_WITHHELD_REASONS: Readonly<Partial<Record<CommentSource, string>>> = {
  MANUAL:
    'Bước soạn chưa nhận nội dung bạn gõ: máy chủ vẫn lấy chữ từ mẫu rồi giữ bản nháp lại chờ người sửa. Hãy chọn "Lấy từ mẫu có sẵn" rồi sửa nội dung ở màn hình duyệt.',
};

/**
 * The half-sentence that follows the quoted phrase in an unsupported-claim
 * warning.
 *
 * Complete `Record` on purpose: a new `kind` in contracts' `UnsupportedClaim`
 * must break this build rather than render an empty half-sentence beside a price
 * somebody is about to publish under their own name.
 *
 * Each line says only what the check knows — the phrase appears nowhere in the
 * material the operator supplied — and never that the phrase is false. Nothing
 * in this system can tell whether "5k/thiệp" is the real price; only the
 * operator can, which is the entire reason the draft is being shown to them.
 */
export const UNSUPPORTED_CLAIM_KIND_MESSAGES: Record<UnsupportedClaim['kind'], string> = {
  PRICE: 'tư liệu thương hiệu bạn đã nhập không có con số giá này.',
  DISCOUNT: 'tư liệu thương hiệu bạn đã nhập không có chương trình giảm giá này.',
  DELIVERY_TIME: 'tư liệu thương hiệu bạn đã nhập không có cam kết thời gian giao này.',
  WARRANTY: 'tư liệu thương hiệu bạn đã nhập không có cam kết bảo hành/đổi trả này.',
  SUPERLATIVE: 'đây là câu tự khen tuyệt đối, không có gì trong tư liệu của bạn chứng minh được.',
};

/**
 * Why a comment was refused — and, in the tone, whether waiting fixes it.
 *
 * The split is deliberate and it is the whole point of this map. A rate limit,
 * a minimum interval or a not-yet-ready browser is a `warning`: nothing is wrong
 * with the text and the same job will go out later untouched. A content or
 * policy refusal is `danger`: this body will be refused again forever until
 * somebody edits it or changes the project's rules. Painting all twenty red
 * sent operators off rewriting perfectly good comments that were only waiting
 * their turn.
 */
export const COMMENT_REJECTION_REASON_LABELS: Record<CommentRejectionReason, Descriptor> = {
  CONTENT_TOO_LONG: { label: 'Nội dung dài hơn giới hạn', tone: 'danger' },
  CONTENT_TOO_SHORT: { label: 'Nội dung ngắn hơn giới hạn', tone: 'danger' },
  PROHIBITED_LINK: { label: 'Chứa liên kết bị cấm', tone: 'danger' },
  PHONE_NUMBER: { label: 'Chứa số điện thoại', tone: 'danger' },
  SENSITIVE_WORD: { label: 'Chứa từ bị cấm', tone: 'danger' },
  DUPLICATE_CONTENT: { label: 'Trùng nội dung đã gửi gần đây', tone: 'danger' },
  OUT_OF_SCOPE: { label: 'Nằm ngoài phạm vi dự án', tone: 'danger' },
  LOW_CONFIDENCE: { label: 'Độ tin cậy dưới ngưỡng của dự án', tone: 'warning' },
  POST_NO_LONGER_ELIGIBLE: { label: 'Bài viết không còn đủ điều kiện', tone: 'warning' },
  RATE_LIMIT_10M: { label: 'Chạm giới hạn 10 phút của profile', tone: 'warning' },
  RATE_LIMIT_1H: { label: 'Chạm giới hạn 1 giờ của profile', tone: 'warning' },
  RATE_LIMIT_1D: { label: 'Chạm giới hạn 1 ngày của profile', tone: 'warning' },
  RATE_LIMIT_PROJECT_1D: { label: 'Chạm giới hạn 1 ngày của dự án', tone: 'warning' },
  MIN_INTERVAL: { label: 'Chưa đủ khoảng cách tối thiểu giữa hai bình luận', tone: 'warning' },
  // The agent's local guard overrules the server's arithmetic by design; this is
  // a refusal to respect, not a bug to route around.
  AGENT_LOCAL_GUARD: { label: 'Agent tự chặn theo giới hạn tại máy', tone: 'warning' },
  EMERGENCY_STOP: { label: 'Đang bật dừng khẩn cấp', tone: 'danger' },
  SUBSCRIPTION_LIMIT: { label: 'Vượt hạn mức của gói dịch vụ', tone: 'danger' },
  PROFILE_MISMATCH: { label: 'Sai profile trình duyệt', tone: 'danger' },
  BROWSER_NOT_READY: { label: 'Trình duyệt chưa sẵn sàng', tone: 'warning' },
  /**
   * The only member here that reports something the operator did on purpose.
   *
   * It arrives when somebody pressed Huỷ and the agent — receiving the withdrawn
   * frame, or catching `local.revoked` in its own guard — had to name the refusal
   * from this closed list. Before it existed the agent borrowed `EMERGENCY_STOP`,
   * so cancelling one comment painted a red "Đang bật dừng khẩn cấp" on the row,
   * which asserts the workspace kill switch is on when it is not.
   *
   * `neutral`, not `danger` and not `warning`: nothing went wrong, nothing needs
   * fixing, and nothing is waiting for its turn. The request was carried out. A
   * red chip on a deliberate cancellation is the same wolf-crying every other
   * tone decision in this file exists to avoid.
   */
  COMMAND_REVOKED: { label: 'Bạn đã huỷ, lệnh bị thu hồi', tone: 'neutral' },
};
